import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  createCategory,
  listCategories,
  updateCategory,
  type AdminCategory,
} from '../../api/admin';
import { Alert, Badge, Button, DataTable, Input, LoadingState } from '../ui';

/**
 * The shelves a story can sit on.
 *
 * A category is never deleted, only taken out of circulation: its slug is in
 * every link and saved filter somebody has, and the stories filed under it keep
 * their shelf either way.
 */
export function AdminCategories() {
  const [categories, setCategories] = useState<AdminCategory[] | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      const answer = await listCategories();
      setCategories(answer.results);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (marker: string, action: () => Promise<unknown>) => {
    setWorking(marker);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking('');
    }
  };

  const add = async () => {
    await run('new', () => createCategory({ name, description }));
    setName('');
    setDescription('');
  };

  return (
    <>
      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      <form
        className="sc-admin__form"
        onSubmit={(event) => {
          event.preventDefault();
          void add();
        }}
      >
        <Input
          label="New category"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
        <Input
          label="What belongs here"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <Button type="submit" isLoading={working === 'new'}>
          Add
        </Button>
      </form>

      {categories === null ? (
        <LoadingState label="Reading the categories…" />
      ) : (
        <DataTable
          caption="Categories on this instance"
          rows={categories}
          rowKey={(category) => category.id}
          columns={[
            {
              key: 'name',
              header: 'Category',
              render: (category) => (
                <>
                  <strong>{category.name}</strong>
                  <span className="sc-admin__muted"> /{category.slug}</span>
                  {!category.is_active && (
                    <>
                      {' '}
                      <Badge tone="neutral">Not offered</Badge>
                    </>
                  )}
                </>
              ),
            },
            {
              key: 'description',
              header: 'What belongs here',
              render: (category) => category.description || '—',
            },
            {
              key: 'stories',
              header: 'Stories',
              numeric: true,
              render: (category) => category.story_count,
            },
            {
              key: 'offered',
              header: 'Offered',
              render: (category) => (
                <Button
                  size="sm"
                  variant="secondary"
                  isLoading={working === category.slug}
                  onClick={() =>
                    void run(category.slug, () =>
                      updateCategory(category.slug, {
                        name: category.name,
                        description: category.description,
                        position: category.position,
                        is_active: !category.is_active,
                      }),
                    )
                  }
                >
                  {category.is_active ? 'Stop offering' : 'Offer again'}
                </Button>
              ),
            },
          ]}
        />
      )}
    </>
  );
}
